/**
 * Code actions — comprehensive coverage of every diagnostic code.
 *
 * A fix reads the diagnostic's `params` (carried across the transport in the
 * LSP `data` field, see `diagnostics.ts`), not the rendered message: the name
 * to rename, the candidate list to qualify with, and `didYouMean` — which the
 * engine now emits as structured candidates instead of a prose tail, so a
 * suggestion becomes a real edit rather than a sentence to read.
 *
 *   CROSS_SPACE_TYPE_NOT_VISIBLE → "Add 'export' to type <name>" (v0.5)
 *   CROSS_SPACE_COUPLING         → "Add 'export' to interface <name>" (v0.5)
 *
 * Each diagnostic code has at least one quick fix:
 *
 *   REQUIRED_NOT_FILLED        → "Fill in <name>" / "Drop <name>"
 *   DROP_NOT_INHERITED         → "Remove this drop"
 *   OVERRIDE_NOT_INHERITED     → "Remove the 'override' keyword"
 *   TYPE_CHANGE_NEEDS_OVERRIDE → "Add 'override' keyword"
 *   ASPECT_DROP_NOT_INHERITED   → "Remove this aspect drop"
 *   PROCESS_BAD_CALLER         → "Remove this step" (error: caller is an interface)
 *   PROCESS_CALLER_TODO        → "Declare module '<name>'" (or add it to the
 *                                existing parent module)
 *   PROCESS_CALLEE_TODO        → "Change to '<candidate>'" per did-you-mean, then
 *                                "Declare '<path>'" (module + interface), or
 *                                "Add interface '<leaf>' to module '<m>'"
 *   PROCESS_BAD_DO_TARGET      → "Change to '<candidate>'" per did-you-mean
 *   PROCESS_CALLEE_UNSPECIFIED → "Use interface '<M.i>'" per declared interface,
 *                                else "Specify the interface" (v0.7)
 *   MULTI_OP_ON_TARGET         → "Remove this duplicate"
 *   RESERVED_NAME              → "Suggest non-reserved alternative"
 *   IN_TARGET_NOT_FOUND        → "Remove the `in <Parent>` clause"
 *   DUPLICATE_TYPE             → "Remove this duplicate"
 *   UNKNOWN_PARENT_TYPE        → no fix (user must define the kind)
 *   UNKNOWN_TYPE               → "Import <name> from <pkg>" when it's a known
 *                                stdlib export; else no fix (user declares it)
 *   SURFACE_CONTAINS_MODULE      → "Remove the nested module"
 *   TYPE_PROCESS_VIEW_FORBIDDEN → no fix (user must rewrite)
 *   CASCADE_MODE_REDEFINED     → no fix
 */

import {
  CodeActionKind,
  type CodeAction,
  type Diagnostic,
  type Range,
  type TextEdit,
  type WorkspaceEdit,
} from "vscode-languageserver";
import type { ResolvedModule, SourceSpan } from "@archlang/engine";
import {
  STDLIB_KIND_PACKAGES,
  buildScopedModuleIndex,
  insertBodyMember,
  resolveScoped,
} from "@archlang/engine";
import type { Workspace } from "../workspace/workspace.js";
import { collectIdTargets, collectStableIds, freshStableId, makeIdMint, type IdTarget } from "./auto-id.js";
import { diagnosticParam, didYouMeanOf } from "./diagnostics.js";

const RESERVED: ReadonlySet<string> = new Set([
  "type",
  "in",
  "aspects",
  "required",
  "cascade",
  "append",
  "override",
  "drop",
  "process",
  "view",
  "alt",
  "par",
  "focus",
  "group",
  "by",
  "layout",
  "include",
  "exclude",
  "true",
  "false",
]);

export function provideCodeActions(
  workspace: Workspace,
  uri: string,
  range: Range,
  diagnostics: readonly Diagnostic[]
): CodeAction[] {
  const out: CodeAction[] = [];
  for (const d of diagnostics) {
    out.push(...actionsFor(workspace, uri, d));
  }
  // Non-diagnostic actions: mint a stable ID for the element at the cursor, or
  // for every ID-less element in the file at once (the auto-id TODO).
  out.push(...autoIdActions(workspace, uri, range));
  return out;
}

function actionsFor(workspace: Workspace, uri: string, d: Diagnostic): CodeAction[] {
  switch (d.code) {
    case "CROSS_SPACE_TYPE_NOT_VISIBLE":
      return crossSpaceTypeFix(workspace, d);
    case "CROSS_SPACE_COUPLING":
      return crossSpaceCouplingFix(workspace, d);
    case "AMBIGUOUS_MODULE_REF":
      return ambiguousModuleFix(workspace, uri, d);
    case "REQUIRED_NOT_FILLED": {
      const name = extractFirstQuoted(d);
      if (!name) return [];
      return [
        edit(uri, d.range, "", { title: `Fill in '${name}'`, replace: { ...d.range, end: d.range.start }, newText: `${name}: TODO\n    ` }),
        edit(uri, d.range, "", { title: `Drop '${name}'`, replace: { ...d.range, end: d.range.start }, newText: `drop ${name}\n    ` }),
      ];
    }
    case "DROP_NOT_INHERITED":
      return [removeRange(uri, d.range, "Remove this 'drop' statement")];
    case "OVERRIDE_NOT_INHERITED":
      return [
        // Remove the "override " keyword prefix; the rest of the decl is fine.
        removePrefix(uri, d.range, "override ", "Remove the 'override' keyword"),
      ];
    case "TYPE_CHANGE_NEEDS_OVERRIDE":
      return [
        prependText(uri, d.range, "override ", "Add 'override' keyword"),
      ];
    case "ASPECT_DROP_NOT_INHERITED":
      return [removeRange(uri, d.range, "Remove this aspect drop")];
    case "PROCESS_BAD_CALLER":
      // A caller that resolved to an interface — always an error (a caller must
      // be a module or actor). Removing the step is the mechanical fix.
      return [removeRange(uri, d.range, "Remove this process step")];
    case "PROCESS_CALLER_TODO":
      // An undefined caller (a `todo` that auto-stubs a stand-in). The
      // constructive fix scaffolds the missing module.
      return declareCallerFix(workspace, uri, d);
    case "PROCESS_CALLEE_TODO":
      // An undefined callee interface. A mis-owned leaf ('Orders.pay' when the
      // interface lives under 'Payments.Api') is repointed straight from the
      // did-you-mean candidates; otherwise scaffold the module + interface.
      return [...didYouMeanFixes(uri, d), ...declareCalleeFix(workspace, uri, d)];
    case "PROCESS_BAD_DO_TARGET":
      // A `do` at a target that exists under a different qualification — the
      // candidates the emit site ranked ARE the fix.
      return didYouMeanFixes(uri, d);
    case "PROCESS_CALLEE_UNSPECIFIED":
      // v0.7: a module callee / trailing-dot whose interface is unspecified —
      // a synthesized handler stands in (a `todo`). The fix names a concrete
      // interface: the module's DECLARED ones first, the `.interface`
      // placeholder last (the only offer when the module declares none).
      return specifyInterfaceFix(workspace, uri, d);
    case "PROCESS_ROLE_CONFLICT":
      // Always an error (a name used as both module and interface). Removing the
      // step is the mechanical fix; renaming one use is the real one.
      return [removeRange(uri, d.range, "Remove this process step")];
    case "MULTI_OP_ON_TARGET":
      return [removeRange(uri, d.range, "Remove this duplicate operation")];
    case "RESERVED_NAME": {
      const name = extractFirstQuoted(d);
      if (!name) return [];
      const suggestion = `${name}_`;
      return [
        edit(uri, d.range, suggestion, {
          title: `Rename to '${suggestion}'`,
          replace: d.range,
          newText: suggestion,
        }),
      ];
    }
    case "IN_TARGET_NOT_FOUND":
      // Remove the `in <Parent>` clause from the declaration line.
      return [removeRange(uri, d.range, "Remove the 'in <Parent>' clause")];
    case "DUPLICATE_TYPE":
      return [removeRange(uri, d.range, "Remove this duplicate type")];
    case "SURFACE_CONTAINS_MODULE":
      return [removeRange(uri, d.range, "Remove the nested module")];
    case "UNKNOWN_TYPE":
      // A bare kind name that didn't resolve. When it's a known stdlib export,
      // offer to import it (one action per publishing package); otherwise the
      // user must declare the kind (no fix).
      return autoImportFix(workspace, uri, d);
    case "ASPECT_BAD_REF": {
      // The diagnostic range covers the bare value token. Quoting it
      // turns the reference into an aspect — the cheap escape hatch when
      // no real module is intended.
      const name = diagnosticParam(d, "name") ?? /module '([^']+)'/.exec(d.message)?.[1];
      if (!name) return [];
      return [
        edit(uri, d.range, "", {
          title: `Convert to aspect "${name}"`,
          replace: d.range,
          newText: `"${name}"`,
        }),
      ];
    }
    default:
      return [];
  }
}

/**
 * The name a fix operates on. Read from `params` — the emit site's own value,
 * round-tripped through the transport's `data` — and only then, for a
 * diagnostic that carries no catalog params at all, from the first quoted run
 * in the message. Parsing prose is the fallback, no longer the mechanism.
 */
const SUBJECT_PARAMS = ["name", "callee", "caller", "qkey", "key", "typeName", "tag", "text"] as const;

function extractFirstQuoted(d: Diagnostic): string | undefined {
  for (const p of SUBJECT_PARAMS) {
    const v = diagnosticParam(d, p);
    if (v) return v;
  }
  return /'([^']+)'/.exec(d.message)?.[1];
}

/** "Did you mean 'X'?" as real actions — one replacement per candidate the
 *  emit site recorded in `params.didYouMean`. */
function didYouMeanFixes(uri: string, d: Diagnostic): CodeAction[] {
  return didYouMeanOf(d).map((c) => ({
    title: `Change to '${c}'`,
    kind: CodeActionKind.QuickFix,
    edit: { changes: { [uri]: [{ range: d.range, newText: c }] } },
  }));
}

/**
 * "Name this process" (PROCESS_ANONYMOUS, v0.7). Two source shapes:
 *   `process { … }`  → drop a placeholder name right after the `process` keyword
 *   bare step(s)     → wrap the step span in `process NewProcess { … }`
 * Best-effort: indentation is left for the author / formatter to tidy.
 */
/**
 * "Qualify as 'A.Auth.login'" (AMBIGUOUS_MODULE_REF): a short module reference
 * that hoists to several modules. Offer one fix per candidate, replacing the
 * ambiguous ref with the qualified path while PRESERVING any interface leaf.
 */
function ambiguousModuleFix(workspace: Workspace, uri: string, d: Diagnostic): CodeAction[] {
  const short = extractFirstQuoted(d);
  // The candidate list is a param the emit site already sorted; the prose scrape
  // stays only for a diagnostic that reached us without one.
  const raw = diagnosticParam(d, "candidates") ?? /candidates:\s*(.+?)\.\s+Qualify/.exec(d.message)?.[1];
  if (!short || !raw) return [];
  const text = workspace.getFile(uri)?.text ?? "";
  const start = offsetOfPosition(text, d.range.start);
  const end = offsetOfPosition(text, d.range.end);
  const cur = text.slice(start, end); // e.g. "Payments.charge" (callee) or "Payments" (caller)
  const trailing = cur.startsWith(short) ? cur.slice(short.length) : ""; // ".charge" / ""
  const cands = raw.split(",").map((c) => c.trim()).filter(Boolean);
  return cands.map((c) => ({
    title: `Qualify as '${c}${trailing}'`,
    kind: CodeActionKind.QuickFix,
    edit: { changes: { [uri]: [{ range: d.range, newText: c + trailing }] } },
  }));
}

/** "Declare module 'X'" (PROCESS_CALLER_TODO): an undefined caller. A DOTTED
 *  caller whose parent already exists is added to that parent (§4.11 scoped
 *  resolution); otherwise a fresh `module X {}` is scaffolded at end of file. */
function declareCallerFix(workspace: Workspace, uri: string, d: Diagnostic): CodeAction[] {
  const name = extractFirstQuoted(d);
  if (!name) return [];
  // The scaffold arrives with a freshly minted stable id — the author never
  // hand-invents one (BEST-PRACTICES: ids are meaningless, tooling mints).
  const mint = makeIdMint(() => workspace.model());
  const segs = name.split(".");
  const leaf = segs[segs.length - 1]!;
  const parents = segs.slice(0, -1);
  if (parents.length > 0) {
    const home = findModuleHome(workspace, uri, parents);
    if (home) {
      const block = renderNested(home.missing, `module #${mint()} ${leaf} {}`, mint);
      const action = insertIntoModule(
        workspace,
        home,
        block,
        `Add module '${leaf}' to module '${home.path.join(".")}'`
      );
      if (action) return [action];
    }
    return [appendDecl(workspace, uri, renderNested(segs.slice(0, -1), `module #${mint()} ${leaf} {}`, mint), `Declare module '${name}'`)];
  }
  return [appendDecl(workspace, uri, `module #${mint()} ${name} {}`, `Declare module '${name}'`)];
}

/** "Declare 'X.Y'" (PROCESS_CALLEE_TODO): an undefined callee interface. When
 *  the owning module (or a prefix of it) already exists, the interface is added
 *  INTO that module — in whichever file declares it — with only the missing
 *  levels nested in. Nothing resolves → scaffold the whole chain at end of file. */
function declareCalleeFix(workspace: Workspace, uri: string, d: Diagnostic): CodeAction[] {
  const name = extractFirstQuoted(d);
  if (!name) return [];
  const segs = name.split(".");
  if (segs.length < 2) return [];
  const mods = segs.slice(0, -1);
  const leaf = segs[segs.length - 1]!;
  // Every module level gets its own fresh id; the interface leaf bears none.
  const mint = makeIdMint(() => workspace.model());
  const home = findModuleHome(workspace, uri, mods);
  if (home) {
    const block = renderNested(home.missing, `interface ${leaf}`, mint);
    const action = insertIntoModule(
      workspace,
      home,
      block,
      `Add interface '${leaf}' to module '${home.path.join(".")}'`
    );
    if (action) return [action];
  }
  return [appendDecl(workspace, uri, renderNested(mods, `interface ${leaf}`, mint), `Declare '${name}'`)];
}

/** "Use interface 'M.i'" (PROCESS_CALLEE_UNSPECIFIED): one action per interface
 *  the referenced module already declares, then the `.interface` placeholder. */
function specifyInterfaceFix(workspace: Workspace, uri: string, d: Diagnostic): CodeAction[] {
  const name = extractFirstQuoted(d);
  if (!name) return [];
  const out: CodeAction[] = [];
  const home = findModuleHome(workspace, uri, name.split("."));
  if (home && home.missing.length === 0) {
    for (const leaf of declaredInterfaceNames(home.module)) {
      out.push(
        edit(uri, d.range, "", {
          title: `Use interface '${name}.${leaf}'`,
          replace: d.range,
          newText: `${name}.${leaf}`,
        })
      );
    }
  }
  out.push(
    edit(uri, d.range, "", {
      title: "Specify the interface",
      replace: d.range,
      newText: `${name}.interface`,
    })
  );
  return out;
}

/** Every interface spelling reachable as `<module>.<leaf>` — the module's own
 *  interfaces plus its surfaces' (§4.11 surface elision), sorted, deduped. */
function declaredInterfaceNames(m: ResolvedModule): string[] {
  const names = new Set<string>();
  for (const i of m.interfaces) names.add(i.name);
  const visitSurfaces = (surfaces: ResolvedModule["surfaces"]): void => {
    for (const s of surfaces) {
      for (const i of s.interfaces) names.add(i.name);
      visitSurfaces(s.surfaces);
    }
  };
  visitSurfaces(m.surfaces);
  return [...names].sort();
}

/** An existing module a missing declaration can be added to, plus the module
 *  levels still to author below it. */
interface ModuleHome {
  /** The uri of the file DECLARING the module — not necessarily the referrer's. */
  readonly uri: string;
  readonly module: ResolvedModule;
  /** The module's full structural path (for the action title). */
  readonly path: readonly string[];
  /** Written segments below the resolved module that don't exist yet. */
  readonly missing: readonly string[];
}

/**
 * The longest prefix of a written module path that resolves to an AUTHORED
 * module visible from `uri` (§4.11 space-scoped hoisting — never hand-rolled
 * name matching). Synthesized stubs and modules whose file the workspace can't
 * read are skipped: an edit needs real text to patch.
 */
function findModuleHome(
  workspace: Workspace,
  uri: string,
  segs: readonly string[]
): ModuleHome | undefined {
  if (segs.length === 0) return undefined;
  const model = workspace.model();
  const index = buildScopedModuleIndex(model);
  const referrerSpace = model.spaces?.get(uri);
  for (let take = segs.length; take >= 1; take--) {
    const hit = resolveScoped(index.get(segs.slice(0, take).join(".")), referrerSpace);
    if (!hit || hit === "ambiguous") continue;
    const m = hit.value;
    if (m.synthesized) continue;
    const file = m.decl.source?.file;
    if (!file || !workspace.getFile(file)) continue;
    return { uri: file, module: m, path: hit.path, missing: segs.slice(take) };
  }
  return undefined;
}

/** Wrap `inner` in the missing module levels, outermost first. */
function renderNested(missing: readonly string[], inner: string, mint: () => string): string {
  const lines: string[] = [];
  missing.forEach((m, i) => lines.push(`${"    ".repeat(i)}module #${mint()} ${m} {`));
  for (const l of inner.split("\n")) lines.push(`${"    ".repeat(missing.length)}${l}`);
  for (let i = missing.length - 1; i >= 0; i--) lines.push(`${"    ".repeat(i)}}`);
  return lines.join("\n");
}

/** Insert `block` as a member of an existing module's body, in its own file. */
function insertIntoModule(
  workspace: Workspace,
  home: ModuleHome,
  block: string,
  title: string
): CodeAction | undefined {
  const text = workspace.getFile(home.uri)?.text;
  if (text === undefined) return undefined;
  let edits;
  try {
    edits = insertBodyMember(text, home.module.decl, block);
  } catch {
    return undefined; // engine refused the patch — fall back to a scaffold
  }
  const changes: TextEdit[] = edits.map((e) => ({
    range: { start: positionOfOffset(text, e.start), end: positionOfOffset(text, e.end) },
    newText: e.newText,
  }));
  return { title, kind: CodeActionKind.QuickFix, edit: { changes: { [home.uri]: changes } } };
}

/**
 * "Import '<name>' from <pkg>" (UNKNOWN_TYPE): a bare kind name that resolves
 * to no local/imported type but IS published by the stdlib. Insert a
 * `use <name> from <pkg>` line at the top of the file — after any leading
 * comment block and existing `use` imports so imports stay grouped. A name
 * published by several packages yields one action per package.
 */
function autoImportFix(workspace: Workspace, uri: string, d: Diagnostic): CodeAction[] {
  const name = extractFirstQuoted(d);
  if (!name) return [];
  const packages = STDLIB_KIND_PACKAGES.get(name);
  if (!packages || packages.length === 0) return [];
  const text = workspace.getFile(uri)?.text ?? "";
  const at = { line: importInsertLine(text), character: 0 };
  return packages.map((pkg) => ({
    title: `Import '${name}' from ${pkg}`,
    kind: CodeActionKind.QuickFix,
    edit: {
      changes: { [uri]: [{ range: { start: at, end: at }, newText: `use ${name} from ${pkg}\n` }] },
    } satisfies WorkspaceEdit,
  }));
}

/** The line to drop a new `use` import on: below a leading comment/blank block,
 *  and below any existing `use` imports so they group together. */
function importInsertLine(text: string): number {
  const lines = text.split("\n");
  let line = 0;
  while (line < lines.length) {
    const t = lines[line]!.trim();
    if (t === "" || t.startsWith("//")) line++;
    else break;
  }
  let afterUse = line;
  for (let i = line; i < lines.length; i++) {
    const t = lines[i]!.trim();
    if (t.startsWith("use ")) afterUse = i + 1;
    else if (t === "" || t.startsWith("//")) continue;
    else break;
  }
  return afterUse;
}

/** Append a fresh declaration at end of file, separated by a blank line. */
function appendDecl(workspace: Workspace, uri: string, decl: string, title: string): CodeAction {
  const text = workspace.getFile(uri)?.text ?? "";
  const at = positionOfOffset(text, text.length);
  const prefix = text.length === 0 || text.endsWith("\n") ? "\n" : "\n\n";
  const ws: WorkspaceEdit = {
    changes: { [uri]: [{ range: { start: at, end: at }, newText: `${prefix}${decl}\n` }] },
  };
  return { title, kind: CodeActionKind.QuickFix, edit: ws };
}

function offsetOfPosition(
  text: string,
  pos: { readonly line: number; readonly character: number }
): number {
  let line = 0;
  let i = 0;
  while (i < text.length && line < pos.line) {
    if (text[i] === "\n") line++;
    i++;
  }
  return Math.min(text.length, i + pos.character);
}

function positionOfOffset(
  text: string,
  offset: number
): { line: number; character: number } {
  const clamped = Math.min(Math.max(0, offset), text.length);
  let line = 0;
  let lineStart = 0;
  for (let i = 0; i < clamped; i++) {
    if (text[i] === "\n") {
      line++;
      lineStart = i + 1;
    }
  }
  return { line, character: clamped - lineStart };
}

// ---------------------------------------------------------------------------
// Cross-space visibility fixes (v0.5). The diagnostic fires at the *reference*
// site, but the fix prepends `export ` at the *declaration* — which may live
// in another file. We locate the declaration via the resolved model.
// ---------------------------------------------------------------------------

interface SrcStart {
  readonly line: number;
  readonly column: number;
}

function exportPrependAction(uri: string, start: SrcStart, title: string): CodeAction {
  // SourceSpan positions are 1-based; LSP is 0-based.
  const pos = { line: Math.max(0, start.line - 1), character: Math.max(0, start.column - 1) };
  const ws: WorkspaceEdit = {
    changes: { [uri]: [{ range: { start: pos, end: pos }, newText: "export " }] },
  };
  return { title, kind: CodeActionKind.QuickFix, edit: ws };
}

function crossSpaceTypeFix(workspace: Workspace, d: Diagnostic): CodeAction[] {
  const name = extractFirstQuoted(d);
  if (!name) return [];
  const space = /space '([^']+)'/.exec(d.message)?.[1];
  const model = workspace.model();
  const spaceOf = (file?: string): string | undefined =>
    file ? model.spaces?.get(file)?.join(".") : undefined;
  const candidates = model.types.filter(
    (t) => t.name === name && !t.exported && t.source?.file
  );
  const decl =
    (space ? candidates.find((t) => spaceOf(t.source?.file) === space) : undefined) ??
    candidates[0];
  if (!decl?.source?.file) return [];
  return [
    exportPrependAction(decl.source.file, decl.source.start, `Add 'export' to type '${name}'`),
  ];
}

function crossSpaceCouplingFix(workspace: Workspace, d: Diagnostic): CodeAction[] {
  // Message: '<from>' (space '<s>') uses '<Module.Iface>' in space '<s2>' …
  const path = /uses '([^']+)'/.exec(d.message)?.[1];
  if (!path) return [];
  const parts = path.split(".");
  if (parts.length < 2) return [];
  const ifaceName = parts[parts.length - 1]!;
  const modPath = parts.slice(0, -1);

  let mods = workspace.model().modules;
  let current: (typeof mods)[number] | undefined;
  for (const seg of modPath) {
    current = mods.find((m) => m.name === seg);
    if (!current) return [];
    mods = current.modules;
  }
  const iface = current?.interfaces.find((i) => i.name === ifaceName);
  const src = iface?.decl.source;
  if (!src?.file) return [];
  return [
    exportPrependAction(src.file, src.start, `Add 'export' to interface '${ifaceName}'`),
  ];
}

// ---------------------------------------------------------------------------
// Auto-ID (the "Auto ID generation" TODO). Stable IDs must be meaningless and
// permanent (BEST-PRACTICES); hand-randomizing is hard, so tooling mints one.
// Two actions, both diagnostic-free:
//   - "Generate stable ID for '<name>'" on the ID-less element at the cursor.
//   - "Generate stable IDs for N element(s)" — every ID-less element in the file.
// Only modules, types, processes, subprocesses, and views carry stable IDs
// (interfaces and surfaces have none); unnamed modules and anonymous processes
// stay ID-less until promoted, so they are skipped.
// ---------------------------------------------------------------------------

function autoIdActions(workspace: Workspace, uri: string, range: Range): CodeAction[] {
  const file = workspace.getFile(uri);
  if (!file) return [];
  const targets: IdTarget[] = [];
  for (const top of file.file.declarations) collectIdTargets(top, targets);
  if (targets.length === 0) return [];

  const taken = collectStableIds(workspace.model());
  const out: CodeAction[] = [];

  // The element under the cursor: the innermost target whose span contains the
  // cursor (latest start among those that contain it).
  const containing = targets
    .filter((t) => spanContains(t.span, range.start))
    .sort((a, b) => spanStartOffset(b.span) - spanStartOffset(a.span));
  const at = containing[0];
  if (at) {
    const id = freshStableId(taken);
    out.push(
      insertIdAction(
        uri,
        at,
        id,
        `Generate stable ID for ${at.noun} '${at.name}'`
      )
    );
  }

  // Bulk: one edit assigning a distinct fresh ID to every ID-less element.
  if (targets.length > 1 || !at) {
    const minted = new Set(taken);
    const edits: TextEdit[] = targets.map((t) => {
      const id = freshStableId(minted);
      minted.add(id);
      return { range: insertRange(t.insert), newText: `#${id} ` };
    });
    out.push({
      title: `Generate stable IDs for ${targets.length} element${targets.length === 1 ? "" : "s"}`,
      kind: CodeActionKind.QuickFix,
      edit: { changes: { [uri]: edits } },
    });
  }
  return out;
}

function insertIdAction(uri: string, t: IdTarget, id: string, title: string): CodeAction {
  const ws: WorkspaceEdit = {
    changes: { [uri]: [{ range: insertRange(t.insert), newText: `#${id} ` }] },
  };
  return { title, kind: CodeActionKind.QuickFix, edit: ws };
}

/** A zero-width insertion range at the start of a source span (1-based → 0-based). */
function insertRange(span: SourceSpan): Range {
  const pos = {
    line: Math.max(0, span.start.line - 1),
    character: Math.max(0, span.start.column - 1),
  };
  return { start: pos, end: pos };
}

function spanStartOffset(span: SourceSpan): number {
  return span.start.offset;
}

/** Whether an LSP position (0-based) falls within a source span (1-based). */
function spanContains(span: SourceSpan, pos: { line: number; character: number }): boolean {
  const sl = span.start.line - 1;
  const sc = span.start.column - 1;
  const el = span.end.line - 1;
  const ec = span.end.column - 1;
  if (pos.line < sl || pos.line > el) return false;
  if (pos.line === sl && pos.character < sc) return false;
  if (pos.line === el && pos.character > ec) return false;
  return true;
}

function removeRange(uri: string, range: Range, title: string): CodeAction {
  const ws: WorkspaceEdit = { changes: { [uri]: [{ range, newText: "" }] } };
  return { title, kind: CodeActionKind.QuickFix, edit: ws };
}

function removePrefix(
  uri: string,
  range: Range,
  prefix: string,
  title: string
): CodeAction {
  const stripRange: Range = {
    start: range.start,
    end: { line: range.start.line, character: range.start.character + prefix.length },
  };
  const ws: WorkspaceEdit = { changes: { [uri]: [{ range: stripRange, newText: "" }] } };
  return { title, kind: CodeActionKind.QuickFix, edit: ws };
}

function prependText(
  uri: string,
  range: Range,
  text: string,
  title: string
): CodeAction {
  const ws: WorkspaceEdit = {
    changes: {
      [uri]: [{ range: { start: range.start, end: range.start }, newText: text }],
    },
  };
  return { title, kind: CodeActionKind.QuickFix, edit: ws };
}

interface EditSpec {
  readonly title: string;
  readonly replace: Range;
  readonly newText: string;
}

function edit(uri: string, _range: Range, _hint: string, spec: EditSpec): CodeAction {
  const e: TextEdit = { range: spec.replace, newText: spec.newText };
  const ws: WorkspaceEdit = { changes: { [uri]: [e] } };
  return { title: spec.title, kind: CodeActionKind.QuickFix, edit: ws };
}

export { RESERVED as RESERVED_KEYWORDS_FOR_CODE_ACTIONS };
